import React, { useState } from 'react';
import { Button } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import SweetAlert2 from 'react-sweetalert2';

import WizardStepper from './components/WizardStepper';
import MerchantStep from './steps/MerchantStep';
import SubaccountsStep from './steps/SubaccountsStep';
import AgentsStep from './steps/AgentsStep';
import ReviewStep from './steps/ReviewStep';
import ProgressStep from './steps/ProgressStep';
import CompleteStep from './steps/CompleteStep';

import useWizardState from './hooks/useWizardState';
import useSubmission from './hooks/useSubmission';

const SetupWizard: React.FC = () => {
  const navigate = useNavigate();
  const userRole = localStorage.getItem('userRole')?.toLowerCase();
  const [swalProps, setSwalProps] = useState<any>({});
  const [createdMerchantId, setCreatedMerchantId] = useState<string | null>(null);

  const {
    state,
    currentStep,
    nextStep,
    prevStep,
    setCurrentStep,
    updateMerchant,
    addSubaccount,
    removeSubaccount,
    updateSubaccount,
    addAgent,
    removeAgent,
    updateAgent,
    addFeeGroupAssignment,
    removeFeeGroupAssignment,
    updateFeeGroupAssignment,
    isStepValid,
    resetWizard,
  } = useWizardState();

  const {
    submissionPlan,
    isSubmitting,
    startSubmission,
    resetSubmission,
  } = useSubmission(
    // onSuccess - store the merchant ID and move to complete step
    (merchantId) => {
      setCreatedMerchantId(merchantId);
      setCurrentStep(4); // Move to Complete step
    },
    // onError - stay on progress step, error is shown in the progress
    () => {
      // Error is already shown in ProgressStep
    }
  );

  // When review step is completed and user clicks Submit, move to Progress step and start submission
  const handleNext = () => {
    if (currentStep === 2) {
      // Move to Progress step and start submission
      setCurrentStep(3);
      startSubmission(state);
    } else {
      nextStep();
    }
  };

  const handleBack = () => {
    prevStep();
  };

  const handleCancel = () => {
    setSwalProps({
      show: true,
      icon: 'warning',
      title: 'Cancel Setup?',
      html: 'Are you sure you want to cancel? All progress will be lost.',
      showCancelButton: true,
      confirmButtonText: 'Yes, cancel',
      cancelButtonText: 'No, continue',
      didClose: () => {
        setSwalProps({});
      },
      preConfirm: () => {
        resetWizard();
        navigate(`/${userRole}/merchants/manage`);
      },
    });
  };

  const handleStartOver = () => {
    resetSubmission();
    resetWizard();
    setCreatedMerchantId(null);
    setCurrentStep(0);
  };

  const handleViewMerchant = () => {
    if (createdMerchantId) {
      navigate(`/${userRole}/merchants/manage/${createdMerchantId}`);
    }
  };

  const handleGoToList = () => {
    navigate(`/${userRole}/merchants/manage`);
  };

  const getTotalSteps = () => 5;
  const isReviewStep = currentStep === 2;
  const isProgressStep = currentStep === 3;
  const canProceed = isStepValid(currentStep);

  // Steps 0-2 allow back/cancel, steps 3-4 don't
  const allowBackOrCancel = currentStep < 3;

  return (
    <div className="inter mx-auto max-w-5xl space-y-6 rounded-xl bg-white p-10 shadow-lg ring-1 ring-gray-100 md:p-12">
      {/* Stepper */}
      <WizardStepper
        currentStep={currentStep}
        merchantType={state.merchant.type}
        onStepClick={(step) => {
          // Only allow clicking on previous steps if we're on steps 0-2
          if (step < currentStep && currentStep < 3) {
            setCurrentStep(step);
          }
        }}
      />

      {/* Step Content */}
      <div className="min-h-[400px] py-4">
        {currentStep === 0 && (
          <MerchantStep
            data={state.merchant}
            onChange={updateMerchant}
            isProcessing={isSubmitting}
          />
        )}

        {currentStep === 1 && state.merchant.type === 'regular' && (
          <SubaccountsStep
            subaccounts={state.subaccounts}
            onAdd={addSubaccount}
            onRemove={removeSubaccount}
            onUpdate={updateSubaccount}
            isProcessing={isSubmitting}
          />
        )}

        {currentStep === 1 && state.merchant.type === 'agent' && (
          <AgentsStep
            agents={state.agents}
            onAdd={addAgent}
            onRemove={removeAgent}
            onUpdate={updateAgent}
            onAddFeeGroupAssignment={addFeeGroupAssignment}
            onRemoveFeeGroupAssignment={removeFeeGroupAssignment}
            onUpdateFeeGroupAssignment={updateFeeGroupAssignment}
            isProcessing={isSubmitting}
          />
        )}

        {currentStep === 2 && (
          <ReviewStep state={state} />
        )}

        {currentStep === 3 && submissionPlan && (
          <ProgressStep
            steps={submissionPlan.steps}
            isComplete={submissionPlan.isComplete}
            hasError={submissionPlan.hasError}
          />
        )}

        {currentStep === 4 && (
          <CompleteStep
            state={state}
            merchantId={createdMerchantId}
            onViewMerchant={handleViewMerchant}
            onGoToList={handleGoToList}
            onCreateAnother={handleStartOver}
          />
        )}
      </div>

      {/* Navigation - Only show for steps 0-2 */}
      {allowBackOrCancel && (
        <div className="flex items-center justify-between border-t border-gray-200 pt-6">
          <div>
            {currentStep > 0 ? (
              <Button
                onClick={handleBack}
                disabled={isSubmitting}
                className="btn btn-default"
              >
                Back
              </Button>
            ) : (
              <Button
                onClick={handleCancel}
                disabled={isSubmitting}
                className="btn btn-default"
              >
                Cancel
              </Button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">
              Step {currentStep + 1} of {getTotalSteps()}
            </span>
            <Button
              onClick={handleNext}
              disabled={!canProceed || isSubmitting}
              isLoading={isSubmitting}
              loadingText="Submitting..."
              className="btn btn-primary"
            >
              {isReviewStep ? 'Submit' : 'Next'}
            </Button>
          </div>
        </div>
      )}

      {/* Error handling for Progress step */}
      {isProgressStep && submissionPlan?.hasError && (
        <div className="flex justify-center gap-4 border-t border-gray-200 pt-6">
          <Button
            onClick={handleStartOver}
            className="btn btn-default"
          >
            Start Over
          </Button>
        </div>
      )}

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: 'custom-swal2-popup-action',
          title: 'custom-swal2-popup-title',
          confirmButton: 'custom-swal2-popup-action custom-swal2-popup-action-confirm',
          cancelButton: 'custom-swal2-popup-action custom-swal2-popup-action-cancel',
        }}
      />
    </div>
  );
};

export default SetupWizard;
